import { ISelfResponse, IUpdateSelfRequest } from '@/shared/interfaces';
import { Input, Modal } from '@/components';
import { R } from '../ProfileResources.resx';
import { R as GlobalResources } from '@/Resources.resx';
import { Button } from '@/components/Button';
import { useForm } from 'react-hook-form';
import {
    isEmail,
    isFullName,
    isPhoneNumber,
    isCprNumber,
} from '@/shared/validators';
import { FormErrorText } from '@/components/FormErrorText';
import { updateSelf } from '@/api/profile/updateSelf';
import { useMutation } from '@tanstack/react-query';
import { Utils } from '@/shared/Utils';

type MyInfoModalProps = {
    selfOnOpen: IUpdateSelfRequest;
    onClose: (selfResponse?: ISelfResponse) => void;
};

export const MyInfoModal = ({ selfOnOpen, onClose }: MyInfoModalProps) => {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IUpdateSelfRequest>({ mode: 'onBlur', values: selfOnOpen });

    const updateSelfMutation = useMutation({
        mutationFn: updateSelf,
    });

    const onSubmit = async (data: IUpdateSelfRequest) => {
        if (updateSelfMutation.isPending) return;
        updateSelfMutation.mutate(data, {
            onSuccess: (data) => onClose(data),
        });
    };

    const onCancel = () => onClose();

    return (
        <Modal onClose={onCancel} title={R.Edit}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <main className="py-6">
                    {!selfOnOpen.Name && (
                        <Input
                            label={R.Name}
                            {...register('Name', {
                                validate: isFullName,
                            })}
                            maxLength={250}
                            error={errors.Name && GlobalResources.NameRequired}
                        />
                    )}
                    {!selfOnOpen.CPR && (
                        <Input
                            label={R.CprNumber}
                            placeholder={GlobalResources.CprPlaceholder}
                            {...register('CPR', {
                                setValueAs: (value) => value.replace('-', ''),
                                validate: isCprNumber,
                            })}
                            maxLength={11}
                            error={errors.CPR && GlobalResources.InvalidCpr}
                            onInput={Utils.sanitizeCprInput}
                        />
                    )}

                    <Input
                        label={R.Email}
                        {...register('Email', {
                            validate: isEmail,
                        })}
                        maxLength={256}
                        error={errors.Email && GlobalResources.InvalidEmail}
                    />

                    <Input
                        label={R.Phone}
                        type="tel"
                        {...register('Mobile', {
                            validate: isPhoneNumber,
                        })}
                        maxLength={20}
                        error={
                            errors.Mobile && GlobalResources.PhoneNumberRequired
                        }
                    />
                    {updateSelfMutation.isError && (
                        <FormErrorText
                            message={updateSelfMutation.error.message}
                        />
                    )}
                </main>
                <footer className="text-right">
                    <Button
                        variant="primary"
                        type="submit"
                        className={
                            updateSelfMutation.isPending
                                ? 'button--loading'
                                : ''
                        }
                    >
                        {R.Save}
                    </Button>
                </footer>
            </form>
        </Modal>
    );
};
